import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import FlagIcon from '@mui/icons-material/Flag';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
  getBudgetMonths,
  getBudgetYears,
  getBudgetForMonth,
  getBudgetAverage,
  updateBudgetItem,
  acceptMissingFlag,
  acceptAllMissingFlags,
  removeAcceptance,
  getValidationForMonth,
  resolveFlag,
} from '../services/budget';

const SECTION_COLORS = {
  INCOME: '#e8f5e9',
  TAXES: '#fff3e0',
  SAVINGS: '#e3f2fd',
  EXPENSES: '#fce4ec',
  'BUDGET SUMMARY': '#f3e5f5',
};

const SECTION_LABELS = {
  INCOME: 'Income',
  TAXES: 'Taxes',
  SAVINGS: 'Savings',
  EXPENSES: 'Expenses',
  'BUDGET SUMMARY': 'Budget Summary',
};

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(val);
}

function formatMonth(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${names[parseInt(mo, 10)]} ${y}`;
}

function formatMonthShort(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = [
    '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${names[parseInt(mo, 10)]} ${y}`;
}

export default function BudgetTemplate() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [budgetData, setBudgetData] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveDialog, setResolveDialog] = useState({ open: false, flag: null });
  const [resolveNotes, setResolveNotes] = useState('');
  const [acceptDialog, setAcceptDialog] = useState({ open: false, flag: null });
  const [acceptNotes, setAcceptNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const isAverageView = selectedMonth === 'average';

  // Load years on mount
  useEffect(() => {
    async function init() {
      try {
        const [yearsRes, monthsRes] = await Promise.all([
          getBudgetYears(),
          getBudgetMonths(),
        ]);
        const yrs = yearsRes.data.years || [];
        const mos = monthsRes.data.months || [];
        setYears(yrs);
        setMonths(mos);
        if (yrs.length > 0) {
          setSelectedYear(yrs[0]);
          setSelectedMonth('average');
        }
      } catch {
        // No data yet
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  // Filter months by selected year
  const filteredMonths = months.filter(
    (m) => selectedYear && m.startsWith(`${selectedYear}-`)
  );

  const loadData = useCallback(async () => {
    if (!selectedMonth || !selectedYear) return;
    setLoading(true);
    try {
      if (selectedMonth === 'average') {
        const budgetRes = await getBudgetAverage(selectedYear);
        setBudgetData(budgetRes.data);
        setValidationData(null);
      } else {
        const [budgetRes, validationRes] = await Promise.all([
          getBudgetForMonth(selectedMonth),
          getValidationForMonth(selectedMonth),
        ]);
        setBudgetData(budgetRes.data);
        setValidationData(validationRes.data);
      }
    } catch {
      setBudgetData(null);
      setValidationData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      loadData();
    }
  }, [selectedMonth, selectedYear, loadData]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setSelectedMonth('average');
  };

  const handleResolve = async () => {
    const flag = resolveDialog.flag;
    setResolveDialog({ open: false, flag: null });
    try {
      await resolveFlag(flag.id, resolveNotes);
      setResolveNotes('');
      loadData();
    } catch {
      // silently fail
    }
  };

  const handleAccept = async () => {
    const flag = acceptDialog.flag;
    setAcceptDialog({ open: false, flag: null });
    try {
      await acceptMissingFlag({
        year: selectedYear,
        section: flag.section,
        parent_category: flag.parent_category,
        category: flag.category,
        notes: acceptNotes,
      });
      setAcceptNotes('');
      setSnackbar({ open: true, message: `Accepted: ${flag.category}` });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to accept flag' });
    }
  };

  const handleSaveItem = async (month, item, newAmount) => {
    try {
      await updateBudgetItem(month, {
        id: item.id,
        section: item.section,
        parent_category: item.parent_category,
        category: item.category,
        type: item.type,
        actual_amount: parseFloat(newAmount),
      });
      setSnackbar({ open: true, message: `Updated: ${item.category}` });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save' });
    }
  };

  const handleReRunAnalysis = async () => {
    setSnackbar({ open: true, message: 'Re-running analysis...' });
    await loadData();
    setSnackbar({ open: true, message: 'Analysis updated' });
  };

  if (loading && years.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (years.length === 0) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Budget Overview</Typography>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No budget data yet. Upload Excel files on the Upload page to see budget actuals here.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const pageTitle = isAverageView
    ? `Budget Overview — ${selectedYear} Monthly Average`
    : `Budget Overview — ${formatMonth(selectedMonth)}`;

  const missingFlags = isAverageView ? (budgetData?.missing_flags || []) : [];
  const unresolvedFlags = missingFlags.filter((f) => !f.accepted);
  const acceptedFlags = missingFlags.filter((f) => f.accepted && !f.stale);
  const staleFlags = missingFlags.filter((f) => f.stale);

  const handleAcceptAll = async () => {
    const flagsToAccept = [...unresolvedFlags, ...staleFlags];
    if (flagsToAccept.length === 0) return;
    try {
      await acceptAllMissingFlags({
        year: selectedYear,
        flags: flagsToAccept.map((f) => ({
          section: f.section,
          parent_category: f.parent_category,
          category: f.category,
        })),
        notes: 'Accepted via Accept All',
      });
      setSnackbar({ open: true, message: `Accepted ${flagsToAccept.length} flags` });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to accept all flags' });
    }
  };

  const handleUnaccept = async (flag) => {
    if (!flag.acceptance_id) return;
    try {
      await removeAcceptance(flag.acceptance_id);
      setSnackbar({ open: true, message: `Unaccepted: ${flag.category}` });
      loadData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to unaccept flag' });
    }
  };

  return (
    <Box>
      {/* Header with year + month selectors */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">{pageTitle}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isAverageView && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReRunAnalysis}
              size="small"
            >
              Re-Run Analysis
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear || ''} label="Year" onChange={handleYearChange}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>View</InputLabel>
            <Select
              value={selectedMonth}
              label="View"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <MenuItem value="average">
                <em>Average (All Months)</em>
              </MenuItem>
              {filteredMonths.length > 0 && <Divider />}
              {filteredMonths.map((m) => (
                <MenuItem key={m} value={m}>{formatMonth(m)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Average view summary alert */}
          {isAverageView && budgetData && (
            <Alert severity={unresolvedFlags.length === 0 && staleFlags.length === 0 ? 'success' : 'info'} icon={<FlagIcon />} sx={{ mb: 3 }}>
              <Typography fontWeight={600}>
                Average across {budgetData.month_count} months ({selectedYear})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {budgetData.months_included?.map(formatMonthShort).join(', ')}
              </Typography>
              {unresolvedFlags.length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {unresolvedFlags.length} categor{unresolvedFlags.length !== 1 ? 'ies' : 'y'} need review
                </Typography>
              )}
              {staleFlags.length > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  {staleFlags.length} previously accepted flag{staleFlags.length !== 1 ? 's' : ''} need re-review (month count changed)
                </Typography>
              )}
              {unresolvedFlags.length === 0 && staleFlags.length === 0 && (
                <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                  All flags resolved or accepted
                </Typography>
              )}
            </Alert>
          )}

          {/* Per-month validation summary */}
          {!isAverageView && validationData && validationData.flags && validationData.flags.length > 0 && (
            <Alert severity="warning" icon={<FlagIcon />} sx={{ mb: 3 }}>
              <Typography fontWeight={600}>
                {validationData.summary.total} validation flag{validationData.summary.total !== 1 ? 's' : ''} for {formatMonth(selectedMonth)}
              </Typography>
              <Typography variant="body2">
                {validationData.summary.mismatch > 0 && `${validationData.summary.mismatch} amount mismatch${validationData.summary.mismatch !== 1 ? 'es' : ''}`}
                {validationData.summary.mismatch > 0 && validationData.summary.unrepresented > 0 && ', '}
                {validationData.summary.unrepresented > 0 && `${validationData.summary.unrepresented} unrepresented categor${validationData.summary.unrepresented !== 1 ? 'ies' : 'y'}`}
              </Typography>
            </Alert>
          )}

          {/* Budget Sections */}
          {budgetData && budgetData.sections && budgetData.sections.map((section) => (
            <Accordion key={section.section} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: SECTION_COLORS[section.section] || '#f5f5f5' }}
              >
                <Typography variant="h6">
                  {SECTION_LABELS[section.section] || section.section}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {isAverageView ? 'Monthly Avg' : 'Actual'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: 80 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.items.map((item, idx) => (
                        <BudgetRow
                          key={item.id || `avg-${idx}`}
                          item={item}
                          isAverageView={isAverageView}
                          monthCount={budgetData.month_count}
                          selectedMonth={selectedMonth}
                          onSave={handleSaveItem}
                        />
                      ))}
                      {section.groups.map((group) => (
                        <React.Fragment key={group.parent_category}>
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              sx={{
                                fontWeight: 700,
                                backgroundColor: '#f5f5f5',
                                textTransform: 'uppercase',
                                fontSize: '0.8rem',
                                letterSpacing: 1,
                              }}
                            >
                              {group.parent_category}
                            </TableCell>
                          </TableRow>
                          {group.items.map((item, idx) => (
                            <BudgetRow
                              key={item.id || `avg-${group.parent_category}-${idx}`}
                              item={item}
                              isAverageView={isAverageView}
                              monthCount={budgetData.month_count}
                              selectedMonth={selectedMonth}
                              onSave={handleSaveItem}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Missing Data Review (average view only) */}
          {isAverageView && missingFlags.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Missing Data Review
                  </Typography>
                  {(unresolvedFlags.length > 0 || staleFlags.length > 0) && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<DoneAllIcon />}
                      onClick={handleAcceptAll}
                    >
                      Accept All ({unresolvedFlags.length + staleFlags.length})
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Categories with $0 in some months. Accept items that are correct (e.g., quarterly bills)
                  or edit per-month data and re-run the analysis.
                </Typography>

                {/* Unresolved + Stale flags */}
                {(unresolvedFlags.length > 0 || staleFlags.length > 0) && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Coverage</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Missing Months</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Amount</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...unresolvedFlags, ...staleFlags].map((flag, idx) => (
                          <MissingFlagRow
                            key={idx}
                            flag={flag}
                            onAccept={(f) => {
                              setAcceptDialog({ open: true, flag: f });
                              setAcceptNotes(f.stale ? (f.acceptance_notes || '') : '');
                            }}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Accepted flags — collapsible */}
                {acceptedFlags.length > 0 && (
                  <Accordion
                    sx={{
                      '&:before': { display: 'none' },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ backgroundColor: '#e8f5e9' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography fontWeight={600}>
                          Accepted ({acceptedFlags.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Coverage</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Missing Months</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Amount</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {acceptedFlags.map((flag, idx) => (
                              <MissingFlagRow
                                key={idx}
                                flag={flag}
                                onAccept={(f) => {
                                  setAcceptDialog({ open: true, flag: f });
                                  setAcceptNotes(f.acceptance_notes || '');
                                }}
                                onUnaccept={handleUnaccept}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* All resolved state */}
                {unresolvedFlags.length === 0 && staleFlags.length === 0 && acceptedFlags.length > 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    All missing data flags have been accepted.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Per-month Validation Flags Detail */}
          {!isAverageView && validationData && validationData.flags && validationData.flags.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Validation Flags</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Transaction Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Budget Actual</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Difference</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationData.flags.map((flag) => (
                        <TableRow key={flag.id}>
                          <TableCell>{flag.category}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={flag.flag_type === 'mismatch' ? 'Mismatch' : 'Unrepresented'}
                              color={flag.flag_type === 'mismatch' ? 'warning' : 'error'}
                              icon={flag.flag_type === 'mismatch' ? <WarningIcon /> : <ErrorIcon />}
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(flag.transaction_total)}</TableCell>
                          <TableCell align="right">{formatCurrency(flag.budget_actual)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: flag.difference > 0 ? 'error.main' : 'success.main' }}
                          >
                            {formatCurrency(flag.difference)}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setResolveDialog({ open: true, flag });
                                setResolveNotes('');
                              }}
                            >
                              Resolve
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Resolve dialog (per-month validation flags) */}
      <Dialog
        open={resolveDialog.open}
        onClose={() => setResolveDialog({ open: false, flag: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve Validation Flag</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Category: <strong>{resolveDialog.flag?.category}</strong>
          </Typography>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog({ open: false, flag: null })}>Cancel</Button>
          <Button onClick={handleResolve} variant="contained" color="primary">Mark Resolved</Button>
        </DialogActions>
      </Dialog>

      {/* Accept dialog (average view missing flags) */}
      <Dialog
        open={acceptDialog.open}
        onClose={() => setAcceptDialog({ open: false, flag: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {acceptDialog.flag?.stale ? 'Re-Review Missing Data' : 'Accept Missing Data'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{acceptDialog.flag?.category}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Average: {formatCurrency(acceptDialog.flag?.average_amount)}/mo
            across {acceptDialog.flag?.months_present} of {acceptDialog.flag?.month_count} months
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Missing in: {acceptDialog.flag?.months_missing?.map(formatMonthShort).join(', ')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Accepting this flag means the $0 months are expected (e.g., quarterly bills,
            seasonal expenses). The average will include $0 months in the calculation.
          </Typography>
          <TextField
            label="Notes (optional, e.g., 'Quarterly expense')"
            fullWidth
            multiline
            rows={2}
            value={acceptNotes}
            onChange={(e) => setAcceptNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialog({ open: false, flag: null })}>Cancel</Button>
          <Button onClick={handleAccept} variant="contained" color="success">
            {acceptDialog.flag?.stale ? 'Re-Accept' : 'Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function BudgetRow({ item, isAverageView, monthCount, selectedMonth, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const isSubtotal = item.is_subtotal;
  const isTotal = item.is_total;
  const isEditable = !isAverageView && !isSubtotal && !isTotal;
  const hasMissing = isAverageView && item.months_missing?.length > 0 && !isSubtotal && !isTotal;
  const isAccepted = item.accepted;
  const isPlaceholder = !isAverageView && !isSubtotal && !isTotal && item.id == null;

  const startEditing = () => {
    setEditValue(item.actual_amount != null ? String(item.actual_amount) : '0');
    setEditing(true);
  };

  const handleSave = () => {
    const val = parseFloat(editValue);
    if (!isNaN(val)) {
      onSave(selectedMonth, item, val);
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <TableRow
      sx={{
        ...(isSubtotal && { backgroundColor: '#fafafa' }),
        ...(isTotal && { backgroundColor: '#eeeeee' }),
        ...(isPlaceholder && { backgroundColor: '#fff8e1' }),
        '&:hover .edit-btn': { opacity: 1 },
      }}
    >
      <TableCell
        sx={{
          pl: isSubtotal || isTotal ? 2 : 4,
          fontWeight: isSubtotal || isTotal ? 700 : 400,
          fontStyle: isSubtotal ? 'italic' : 'normal',
        }}
      >
        {item.category}
      </TableCell>
      <TableCell
        align="right"
        sx={{ fontWeight: isSubtotal || isTotal ? 700 : 400 }}
      >
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <TextField
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              sx={{ width: 120 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <IconButton size="small" color="success" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {formatCurrency(item.actual_amount)}
            {hasMissing && !isAccepted && (
              <Tooltip title={`$0 in ${item.months_missing.length} of ${monthCount} months: ${item.months_missing.map(formatMonthShort).join(', ')}`}>
                <WarningIcon sx={{ color: 'warning.main', fontSize: 14, ml: 0.5 }} />
              </Tooltip>
            )}
            {isEditable && (
              <IconButton
                size="small"
                className="edit-btn"
                sx={{ opacity: 0, ml: 0.5, transition: 'opacity 0.2s' }}
                onClick={startEditing}
              >
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        )}
      </TableCell>
      <TableCell align="center">
        {isAverageView ? (
          !isSubtotal && !isTotal && (
            (isAccepted || (!hasMissing && item.actual_amount != null && item.actual_amount > 0)) ? (
              <Tooltip title={isAccepted ? 'Accepted' : `Present in all ${monthCount} months`}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
              </Tooltip>
            ) : hasMissing ? (
              <Tooltip title={`Missing: ${item.months_missing.map(formatMonthShort).join(', ')}`}>
                <Chip
                  size="small"
                  label={`${item.months_present}/${monthCount}`}
                  color={item.stale ? 'warning' : item.months_present === 0 ? 'error' : 'warning'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              </Tooltip>
            ) : null
          )
        ) : (
          !isSubtotal && !isTotal && (
            isPlaceholder ? (
              <Tooltip title="No data for this month — click pencil to add">
                <Chip
                  size="small"
                  label="No data"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              </Tooltip>
            ) : item.actual_amount != null && item.actual_amount > 0 ? (
              <Tooltip title="Has data">
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
              </Tooltip>
            ) : null
          )
        )}
      </TableCell>
    </TableRow>
  );
}

function MissingFlagRow({ flag, onAccept, onUnaccept }) {
  return (
    <TableRow
      sx={{
        backgroundColor: flag.accepted ? '#e8f5e9' : flag.stale ? '#fff8e1' : 'inherit',
      }}
    >
      <TableCell>{flag.category}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={SECTION_LABELS[flag.section] || flag.section}
          sx={{
            backgroundColor: SECTION_COLORS[flag.section] || '#f5f5f5',
            fontWeight: 500,
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="body2"
          sx={{
            color: flag.months_present === 0 ? 'error.main'
              : flag.months_present < flag.month_count / 2 ? 'warning.main'
              : 'text.primary',
            fontWeight: 600,
          }}
        >
          {flag.months_present} / {flag.month_count}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={flag.months_missing.map(formatMonthShort).join(', ')}>
          <Chip
            size="small"
            label={`${flag.months_missing.length} month${flag.months_missing.length !== 1 ? 's' : ''}`}
            color={flag.accepted ? 'success' : flag.months_present === 0 ? 'error' : 'warning'}
            variant="outlined"
          />
        </Tooltip>
      </TableCell>
      <TableCell align="right">{formatCurrency(flag.average_amount)}</TableCell>
      <TableCell align="center">
        {flag.accepted && !flag.stale ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title={flag.acceptance_notes || 'Accepted'}>
              <Chip size="small" label="Accepted" color="success" icon={<CheckCircleIcon />} />
            </Tooltip>
            {onUnaccept && (
              <Button
                size="small"
                variant="text"
                color="error"
                sx={{ minWidth: 'auto', fontSize: '0.7rem', px: 0.5 }}
                onClick={() => onUnaccept(flag)}
              >
                Undo
              </Button>
            )}
          </Box>
        ) : flag.stale ? (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => onAccept(flag)}
          >
            Re-Review
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onAccept(flag)}
          >
            Accept
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
